// ============================================================
// TMR Portal Admin — as diferenças entre duas versões, recortadas para leitura
// ============================================================
import type { DiffLine } from './contract';

/**
 * What the comparison shows: a line, or an unchanged stretch folded away — carried whole, so the screen can
 * open it where it is.
 */
export type DiffItem =
  | { kind: 'line'; line: DiffLine }
  | { kind: 'gap'; lines: DiffLine[] };

/**
 * The comparison as it is read: every changed line, a little of what surrounds it, and the rest folded.
 *
 * A contract is long and a new version usually touches a clause or two. Showing every line buries the
 * change; showing only the changed lines loses where in the contract it happened. The unchanged lines next
 * to each change are what tells the reader which clause they are looking at — in Markdown a paragraph is a
 * line, so one line of context is a whole paragraph of it.
 *
 * @param lines The comparison, line by line, as the backend returns it.
 * @param context How many unchanged lines to keep on each side of a change.
 */
export function diffItems(lines: DiffLine[], context = 1): DiffItem[] {
  const keep = lines.map(() => false);

  lines.forEach((line, index) => {
    if (line.kind === 'Unchanged') return;

    for (let near = Math.max(0, index - context); near <= Math.min(lines.length - 1, index + context); near++) {
      keep[near] = true;
    }
  });

  const items: DiffItem[] = [];
  let folded: DiffLine[] = [];

  lines.forEach((line, index) => {
    if (keep[index]) {
      if (folded.length > 0) items.push({ kind: 'gap', lines: folded });
      folded = [];
      items.push({ kind: 'line', line });
      return;
    }

    folded.push(line);
  });

  if (folded.length > 0) items.push({ kind: 'gap', lines: folded });

  return items;
}
